import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ui } from '@procuros/datachecks';

import { InvoiceDiscrepanciesServiceSingleton } from 'services/invoiceDiscrepancies';
import { DisplayModes } from 'services/webedi/types';
import { DocumentType } from 'support/types';
import { DataTable, DataTableHandle } from 'components/DataTable';
import { DataTableProvider, useDataTableContext } from 'components/DataTable/contexts/DataTableContext';
import { DocumentSectionAnnotation } from 'components/WebEDI/DocumentSectionAnnotation/DocumentSectionAnnotation';
import { useGenerateLineItemsColumns } from 'components/WebEDI/useGenerateLineItemsColumns';
import { WebEDISection } from 'components/WebEDI/WebEDISection/WebEDISection';

export const LineItemsSection = memo(({ config, dataPrefix, documentType }: LineItemsSectionProps) => {
  const { options, fields } = config;
  if (!fields?.length) {
    return null;
  }

  return (
    <DataTableProvider name={dataPrefix}>
      <LineItemsTable
        config={config}
        numberOfPinnedColumns={options?.pinned_column_count ?? 3}
        documentType={documentType}
        dataPrefix={dataPrefix}
      />
    </DataTableProvider>
  );
});

LineItemsSection.displayName = 'LineItemsSection';

const LineItemsTable = ({
  dataPrefix,
  config,
  numberOfPinnedColumns,
  documentType,
}: {
  dataPrefix: string;
  config: LineItemsSectionProps['config'];
  numberOfPinnedColumns: number | undefined;
  documentType: DocumentType;
}) => {
  const { t } = useTranslation();
  const { fields } = useDataTableContext();
  const { columns, pinnedColumns } = useGenerateLineItemsColumns({
    config: config.fields,
    mode: DisplayModes.view,
    documentType,
    numberOfPinnedColumns,
  });

  const dataTableRef = useRef<DataTableHandle>(null);

  useEffect(() => {
    // Expand first: a collapsed row renders no `data-field` for its embedded items, so
    // `scrollToNavigableItem` would fail to find the field it is navigating to.
    InvoiceDiscrepanciesServiceSingleton.registerVirtualizedScroll(dataPrefix, (index) => {
      dataTableRef.current?.expandRow(index);
      dataTableRef.current?.scrollToIndex(index);
    });
    return () => InvoiceDiscrepanciesServiceSingleton.unregisterVirtualizedScroll(dataPrefix);
  }, [dataPrefix]);

  return (
    <WebEDISection
      header={{
        title: t(`invoiceVerification:detailsPage.sections.${dataPrefix}`, {
          count: fields.length,
        }),
        spacing: 'medium',
        description: config.description,
      }}
    >
      <div className="space-y-4">
        <DocumentSectionAnnotation path={dataPrefix} sectionConfig={config} />

        <DataTable
          ref={dataTableRef}
          pinnedColumns={pinnedColumns}
          key={DisplayModes.view}
          id={dataPrefix}
          meta={{ dataPrefix, mode: DisplayModes.view, currencyFieldPath: 'currency' }}
          columns={columns}
          data={fields}
          enableGlobalSearch={true}
          searchPositionStrategy="absolute"
          emptyState={t('webedi:line_items.empty_state.view.text')}
        />
      </div>
    </WebEDISection>
  );
};

type LineItemsSectionProps = {
  config: Omit<ui.LineItemsSection, 'type'>;
  mode: DisplayModes;
  documentType: DocumentType;
  dataPrefix: 'unmatched_line_items' | 'matched_line_items';
};

/**
 * i18n keys
 * t('invoiceVerification:detailsPage.sections.unmatched_line_items', { count: fields.length });
 * t('invoiceVerification:detailsPage.sections.matched_line_items', { count: fields.length });
 */
